/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link, useParams } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import SignalPopup from './SignalPopup';
import ExportButton from '../active-users/ExportButton';
import DateRangeBar, { fmt } from '../active-users/DateRangeBar';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import PauseHistorySummaryCards from '../pause/PauseHistorySummaryCards';
import HeartRateTopTagsPanel from './HeartRateTopTagsPanel';
import '../active-users/active-users.css';
import './HeartRateSessions.css';

const LIMIT = 20;
const EXPORT_COLUMNS = [
  { key: 'row_no', label: 'No' },
  { key: 'name', label: 'Name' },
  { key: 'heart_rate', label: 'Heart rate' },
  { key: 'mood_type', label: 'Mood type' },
  { key: 'step_count', label: 'Step count' },
  { key: 'pause_count', label: 'Pause count' },
  { key: 'glucose', label: 'Glucose' },
  { key: 'blood_pressure', label: 'Blood Pressure' },
  { key: 'oxygen', label: 'Oxygen' },
  { key: 'tag_name', label: 'Tag' },
  { key: 'location_tag', label: 'Location tag' },
  { key: 'note', label: 'Note' },
  { key: 'start_time_stamp', label: 'Start' },
  { key: 'end_time_stamp', label: 'End' },
  { key: 'deleted', label: 'Deleted' },
];

// "YYYY-MM-DD HH:mm:ss" → "dd/MM/yyyy HH:mm:ss" (matches the legacy detail table).
const formatDateTime = (dateStr) => {
  if (!dateStr || dateStr === '-') return '-';
  const [datePart, timePart] = String(dateStr).split(' ');
  if (!datePart) return '-';
  const [year, month, day] = datePart.split('-');
  return `${day}/${month}/${year}${timePart ? ' ' + timePart : ''}`;
};

const HeartRateSessionList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = id ? atob(id) : '';

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [topTags, setTopTags] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFrom, setDisplayFrom] = useState(null);
  const [displayTo, setDisplayTo] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupData, setPopupData] = useState(null);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // No default date window — the detail page opens all-time; the user can
    // narrow with the date range bar.
    refreshToken();
  }, []);

  useEffect(() => {
    if (token) loadData(1, fromDate, toDate, searchValue, sortField, sortOrder);
  }, [token]);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const scope = () => (userId ? `&user_id=${userId}` : '');

  const loadData = async (p, f, t, s, sf, so) => {
    setLoading(true); setError('');
    const listQs = `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${s}&sortField=${sf}&sortOrder=${so}${scope()}`;
    const filterQs = `from_date=${f}&to_date=${t}&search=${s}${scope()}`;
    try {
      const [list, sum] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/heart-rate-sessions?${listQs}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/heart-rate-sessions-summary?${filterQs}`, auth()),
      ]);
      setRows(list.data.data || []);
      setTotalItem(list.data.totalItem || 0);
      setTopTags(list.data.topTags || []);
      setSummary(sum.data.data || null);
    } catch (err) {
      if (err?.response?.status === 401) setError('auth'); else setError('load');
    } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    loadData(pageNumber, fromDate, toDate, searchValue, sortField, sortOrder);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFrom(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayTo(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFrom(fromStr ? new Date(fromStr) : null); setDisplayTo(toStr ? new Date(toStr) : null);
    setPage(1); loadData(1, fromStr, toStr, searchValue, sortField, sortOrder);
  };
  const handleSearch = () => { setPage(1); loadData(1, fromDate, toDate, searchValue, sortField, sortOrder); };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadData(1, fromDate, toDate, searchValue, field, order);
  };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFrom(null); setDisplayTo(null);
    setPreset(null); setSearchValue(''); setSortField(''); setSortOrder(''); setPage(1);
    loadData(1, '', '', '', '', '');
  };

  const openSignal = (row) => { setPopupData(row.signal_hr_detail); setIsPopupOpen(true); };
  const closePopup = () => { setIsPopupOpen(false); setPopupData(null); };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/heart-rate-sessions-export?from_date=${fromDate}&to_date=${toDate}&search=${searchValue}&sortField=${sortField}&sortOrder=${sortOrder}${scope()}`,
      auth());
    const data = res.data?.data?.rows || res.data?.data || [];
    return data.map((r) => ({
      ...r,
      heart_rate: `${r.min_heart_rate ?? ''}-${r.max_heart_rate ?? ''}`,
      start_time_stamp: formatDateTime(r.start_time_stamp),
      end_time_stamp: formatDateTime(r.end_time_stamp),
      location_tag: r.location_tag || '-',
      deleted: r.is_deleted === 'true' ? 'Yes' : 'No',
    }));
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );
  const hasFilters = !!(searchValue || fromDate || toDate || sortField);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

  const summaryCards = summary ? [
    { label: 'Total sessions', value: Number(summary.total_sessions || 0).toLocaleString() },
    { label: 'Distinct active days', value: Number(summary.active_days || 0).toLocaleString() },
    { label: 'Avg max heart rate', value: summary.avg_max_heart_rate == null ? null : `${summary.avg_max_heart_rate} bpm` },
  ] : [];

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Health › Heart rate sessions › <b>Session history</b></span>
            <span className="au-route">/heart-rate-sessions</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/user-heart-rate-sessions">← Back</Link></span>
          </div>
          <h1 className="au-title">Session history</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the session history. Please try again." onRetry={() => loadData(page, fromDate, toDate, searchValue, sortField, sortOrder)} />
          ) : (
            <>
              <PauseHistorySummaryCards cards={summaryCards} />

              <HeartRateTopTagsPanel tags={topTags} totalSessions={summary?.total_sessions ?? totalItem} />

              <DateRangeBar
                leading={(
                  <input className="au-input" type="text" placeholder="Search sessions" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} aria-label="Search sessions" />
                )}
                preset={preset}
                displayFrom={displayFrom}
                displayTo={displayTo}
                hasRange={hasFilters}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="hr-detail-range-note">
                The date range filters sessions by their <b>activity date (UTC)</b> — the session timestamp for this user.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all sessions (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No sessions in this range" hasFilters={hasFilters} onClear={clearFilter}
                  message="No heart rate sessions match the current filters." />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Heart rate session records</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="heart-rate-session-history.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table au-mau-table hr-table" data-testid="hr-detail-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Heart rate</th><th>Mood type</th><th>Step count</th>
                          <th>Pause count</th><th>Glucose</th><th>Blood Pressure</th><th>Oxygen</th>
                          <SortTh field="tag_name" label="Tag" />
                          <th>Location tag</th>
                          <th>Note</th><th>Start</th><th>End</th><th>Deleted</th><th>Signal Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id}>
                            <td data-label="No" className="tnum">{r.id}</td>
                            <td data-label="Name" className="au-ellipsis">{r.name}</td>
                            <td data-label="Heart rate" className="tnum">{r.min_heart_rate}-{r.max_heart_rate}</td>
                            <td data-label="Mood type">{r.mood_type}</td>
                            <td data-label="Step count" className="tnum">{r.step_count}</td>
                            <td data-label="Pause count" className="tnum">{r.pause_count}</td>
                            <td data-label="Glucose" className="tnum">{r.glucose}</td>
                            <td data-label="Blood Pressure">{r.blood_pressure}</td>
                            <td data-label="Oxygen" className="tnum">{r.oxygen}</td>
                            <td data-label="Tag">{r.tag_name}</td>
                            <td data-label="Location tag" className="au-ellipsis">{r.location_tag || '-'}</td>
                            <td data-label="Note" className="au-ellipsis">{r.note}</td>
                            <td data-label="Start">{formatDateTime(r.start_time_stamp)}</td>
                            <td data-label="End">{formatDateTime(r.end_time_stamp)}</td>
                            <td data-label="Deleted">{r.is_deleted === 'true' ? 'Yes' : 'No'}</td>
                            <td data-label="Signal Details">
                              <button type="button" className="au-link" aria-label={`View signal details for record ${r.id}`} onClick={() => openSignal(r)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                <img src={`${process.env.PUBLIC_URL}/images/view.png`} alt="" title="Use this option to show signal details" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}

              {isPopupOpen && <SignalPopup data={popupData} onClose={closePopup} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeartRateSessionList;
